var React = require('react');
var ReactDOM = require('react-dom');
var { useState, useEffect, useMemo, useRef } = require('react');
var { useSelector } = require('react-redux');
var { useTranslation } = require('react-i18next');
var $ = require('jquery');
var sensorsManager = require('sensorsManager');
var ChipButton = require('ChipButton');
var { ReportCase } = require('@managers/CasesManager');
var redAlertIcon = require('images/icons/red_alert_icon.svg');
var { SuccessMessage } = require('./EventModal');
require('figmaCSS');

const ReportIssueModal = ({ modalId, onDismiss, onSuccess }) => {
  const { t } = useTranslation();
  const selectedDistribution = useSelector(
    (state) => state.distribution_data.selected_entities.distribution,
  );
  const selectedFarmingUnit = useSelector(
    (state) => state.distribution_data.selected_entities.farming_unit,
  );

  const [assumption, setAssumption] = useState('');
  const [selectedSensorIds, setSelectedSensorIds] = useState([]);
  const [comment, setComment] = useState('');
  const [fieldId, setFieldId] = useState(null);
  const [fieldName, setFieldName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [successToast, setSuccessToast] = useState(null);
  const [currentField, setCurrentField] = useState(null);
  const successToastRef = useRef(null);

  // Assumptions that require sensor selection
  const sensorRelatedAssumptions = [
    'air_burst',
    'bad_values',
    'continuous_irrigation',
    'pressure_higher_than_design',
    'pressure_lower_than_design',
    'missed_irrigation',
    'communication_no_recent_data',
    'sensor_not_responding_to_irrigation',
    'extract_sensor',
  ];

  // Assumptions that allow selecting more than one sensor and submit one
  // case per sensor.
  const multiSensorAssumptions = ['extract_sensor'];
  const isMultiSensorAssumption = multiSensorAssumptions.includes(assumption);

  const resetForm = (preserveSuccessToast = false) => {
    setAssumption('');
    setSelectedSensorIds([]);
    setComment('');
    setFieldId(null);
    setFieldName('');
    setCurrentField(null);
    setIsSubmitting(false);
    setSubmitError('');
    if (!preserveSuccessToast) {
      setSuccessToast(null);
      successToastRef.current = null;
    }
  };

  const handleClose = () => {
    $('#' + modalId).modal('hide');
  };

  const handleAssumptionChange = (selectedAssumption) => {
    setAssumption(selectedAssumption);
    setSubmitError('');
    // Reset sensors when assumption changes (don't preserve cross-mode state)
    setSelectedSensorIds([]);
  };

  const handleSensorSelect = (sensorId) => {
    setSelectedSensorIds((prev) => {
      const isCurrentlySelected = prev.includes(sensorId);
      if (isMultiSensorAssumption) {
        return isCurrentlySelected
          ? prev.filter((id) => id !== sensorId)
          : [...prev, sensorId];
      }
      // Single selection - toggle off if same, otherwise replace
      return isCurrentlySelected ? [] : [sensorId];
    });
  };

  // Returns the URL of a successful case or null/throws on failure.
  const submitSingleCase = (sensorId) => {
    const sensorData =
      sensorId && currentField?.sensors ? currentField.sensors[sensorId] : null;

    const sensorPayload = sensorData
      ? {
          transmitter_id: sensorData.transmitter_id,
          sensorCoordinates: sensorData.coordinates,
          sensorType: sensorData.type,
          serial_number: sensorId,
        }
      : {};

    return ReportCase({
      assumption,
      comment,
      distributionName: selectedDistribution?.name || '',
      farmingUnitName: selectedFarmingUnit?.name || '',
      fieldName: fieldName || '',
      mondayId: selectedFarmingUnit?.mondayID || '',
      distributionId: selectedDistribution?.id || '',
      farmingUnitId: selectedFarmingUnit?.id || '',
      fieldId: fieldId || '',
      ...sensorPayload,
    }).then((response) => {
      let responseData = response;

      if (response?.statusCode && response.statusCode >= 400) {
        const err = new Error('Report case failed');
        err.statusCode = response.statusCode;
        throw err;
      }

      if (response?.body && typeof response.body === 'string') {
        try {
          responseData = JSON.parse(response.body);
        } catch (e) {
          console.error('Failed to parse response body:', e);
        }
      }

      if (responseData?.error) {
        throw new Error(responseData.error);
      }

      return responseData?.url_to_case || '';
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!assumption) {
      return;
    }

    const sensorIdsToSubmit = sensorRelatedAssumptions.includes(assumption)
      ? selectedSensorIds
      : [null];

    if (
      sensorRelatedAssumptions.includes(assumption) &&
      sensorIdsToSubmit.length === 0
    ) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    Promise.allSettled(sensorIdsToSubmit.map((id) => submitSingleCase(id)))
      .then((results) => {
        const failed = results.filter((r) => r.status === 'rejected');
        const succeeded = results.filter((r) => r.status === 'fulfilled');

        if (failed.length > 0) {
          if (typeof mixpanel !== 'undefined') {
            mixpanel.track('Issue Submission Failed', {
              'Field ID': fieldId,
              'Field Name': fieldName,
              Assumption: assumption,
              Sensors: sensorIdsToSubmit.filter(Boolean).join(', '),
              'Total Cases': sensorIdsToSubmit.length,
              'Failed Cases': failed.length,
              Comment: comment,
              'Error Message': failed[0].reason?.message || 'Unknown error',
            });
          }
          setSubmitError(t('report_issue_error'));
          return;
        }

        if (typeof mixpanel !== 'undefined') {
          mixpanel.track('Issue Submitted Successfully', {
            'Field ID': fieldId,
            'Field Name': fieldName,
            Assumption: assumption,
            Sensors: sensorIdsToSubmit.filter(Boolean).join(', '),
            'Total Cases': sensorIdsToSubmit.length,
            Comment: comment,
          });
        }

        // Use the first case URL when there is only one; otherwise omit
        // the link so the toast doesn't suggest a single canonical case.
        const firstUrl = succeeded[0]?.value || '';
        if (succeeded.length === 1 && firstUrl) {
          const toastData = { url: firstUrl };
          setSuccessToast(toastData);
          successToastRef.current = toastData;
        } else if (succeeded.length > 1) {
          const toastData = { url: '' };
          setSuccessToast(toastData);
          successToastRef.current = toastData;
        }

        setTimeout(() => {
          $('#' + modalId).modal('hide');
          if (onSuccess) {
            onSuccess();
          }
        }, 100);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  useEffect(() => {
    const handleShowModal = (event) => {
      var field_id = '';
      var field_name = '';
      var selected_field = null;

      if (event.relatedTarget) {
        var button = $(event.relatedTarget);
        field_id = button[0].dataset.id;
      }

      if (field_id && selectedFarmingUnit?.fields) {
        selected_field = selectedFarmingUnit.fields[field_id];
        field_name = selected_field?.name || '';
      }

      setFieldId(field_id);
      setFieldName(field_name);
      setCurrentField(selected_field);
      setAssumption('');
      setSelectedSensorIds([]);
      setComment('');
      setIsSubmitting(false);
      setSubmitError('');
      setSuccessToast(null); // Clear success toast when modal opens
      successToastRef.current = null;
    };

    const handleHideModal = (event) => {
      // Prevent closing when clicking outside (backdrop click)
      // Bootstrap sets the target to the modal element when clicking backdrop
      const $modal = $('#' + modalId);
      if (
        event &&
        event.target &&
        event.target === $modal[0] &&
        !event.isTrigger
      ) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        return false;
      }
      // Preserve success toast when closing after successful submission
      resetForm(successToastRef.current !== null);
      if (onDismiss) {
        onDismiss();
      }
    };

    const handleBackdropClick = (event) => {
      // Prevent closing when clicking on the backdrop
      if (event.target === event.currentTarget) {
        event.preventDefault();
        event.stopPropagation();
        return false;
      }
    };

    $('#' + modalId).on('show.bs.modal', handleShowModal);
    $('#' + modalId).on('hide.bs.modal', handleHideModal);
    $('#' + modalId).on('click', handleBackdropClick);

    return () => {
      $('#' + modalId).off('show.bs.modal', handleShowModal);
      $('#' + modalId).off('hide.bs.modal', handleHideModal);
      $('#' + modalId).off('click', handleBackdropClick);
    };
  }, [modalId, selectedFarmingUnit, onDismiss]);

  useEffect(() => {
    if (!successToast) {
      return;
    }
    const timeoutId = setTimeout(() => setSuccessToast(null), 6000);
    return () => clearTimeout(timeoutId);
  }, [successToast]);

  // Get available sensors from current field - filter for gt/mt sensors like SearchContainer
  const availableSensors = useMemo(() => {
    if (!currentField?.sensors) return [];
    return Object.values(currentField.sensors).filter((sensor) => {
      if (
        sensor?.transmitter_id?.toLowerCase().startsWith('gt') ||
        sensor?.transmitter_id?.toLowerCase().startsWith('mt')
      ) {
        return sensor;
      }
      return false;
    });
  }, [currentField]);

  // Get WP sensors for color indexing
  const wpSensors = useMemo(() => {
    if (!currentField?.sensors) return {};
    return sensorsManager.getWPSensors(currentField.sensors);
  }, [currentField]);

  const wpSensorIds = Object.keys(wpSensors).sort();

  const getSensorInfo = (sensor, index) => {
    let sensorColor = '';
    let sensorType = sensor.type?.includes('piezometer')
      ? ' (e-piezo)'
      : sensor.type?.includes('wfr') && !sensor.type?.includes('tal-gil')
        ? ' (Flow Rate)'
        : sensor.type?.includes('pressure')
          ? ' (pressure)'
          : '';

    if (sensorType === '') {
      const sensorIndex = wpSensorIds.indexOf(sensor.id);
      sensorColor = sensorsManager.sensorColorByIndex(
        sensorIndex >= 0 ? sensorIndex : index,
        currentField?.settings?.field_state,
      );
    } else if (sensorType === ' (e-piezo)') {
      sensorColor = '#662F6F';
    } else if (sensorType === ' (Flow Rate)') {
      sensorColor = '#3A8500';
    } else if (sensorType === ' (pressure)') {
      sensorColor = '#1565C0';
    }

    return { sensorColor, sensorType };
  };

  const isFormValid =
    assumption &&
    (!sensorRelatedAssumptions.includes(assumption) ||
      selectedSensorIds.length > 0);

  // Assumption options - organized by sensor and field issues
  const assumptionOptions = [
    // Sensor Issues Placeholder
    {
      value: 'placeholder_sensor',
      title: t('report_issue_sensor_issues'),
      isPlaceholder: true,
    },
    { value: 'air_burst', title: t('air_burst') },
    { value: 'bad_values', title: t('bad_values') },
    {
      value: 'continuous_irrigation',
      title: t('continuous_irrigation'),
    },
    {
      value: 'pressure_higher_than_design',
      title: t('high_pressure_detected'),
    },
    {
      value: 'pressure_lower_than_design',
      title: t('low_pressure_detected'),
    },
    {
      value: 'missed_irrigation',
      title: t('missed_irrigation'),
    },
    {
      value: 'communication_no_recent_data',
      title: t('no_transmission'),
    },
    {
      value: 'sensor_not_responding_to_irrigation',
      title: t('sensor_not_responding_to_irrigation'),
    },
    { value: 'extract_sensor', title: t('extract_sensor') },
    // Field Issues Placeholder
    {
      value: 'placeholder_field',
      title: t('report_issue_field_issues'),
      isPlaceholder: true,
    },
    { value: 'drying_trend', title: t('drying_trend') },
    {
      value: 'long_dry_period',
      title: t('long_dry_period'),
    },
    { value: 'wetting_trend', title: t('wetting_trend') },
  ];

  return (
    <>
      <div
        className='modal fade'
        id={modalId}
        tabIndex='-1'
        role='dialog'
        aria-labelledby='reportIssueModalLabel'
        aria-hidden='true'
        data-backdrop='static'
        data-keyboard='false'
      >
        <div
          className='modal-dialog'
          role='document'
          style={{
            maxWidth: '440px',
            width: '100%',
            marginTop: '80px',
            marginBottom: 'auto',
          }}
        >
          <div
            className='modal-content'
            style={{
              borderRadius: '10px',
              border: '1px solid var(--color-grey-91)',
              overflow: 'hidden',
              background: 'var(--color-grey-100)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className='modal-header'
              style={{
                background: 'var(--color-grey-100)',
                borderBottom: '1px solid var(--color-grey-91)',
                borderRadius: '10px 10px 0 0',
                padding: '16px 24px',
              }}
            >
              <div style={{ flex: 1 }}>
                <h5
                  className='modal-title text-grey45'
                  id='reportIssueModalLabel'
                  style={{
                    margin: 0,
                    marginBottom: '4px',
                    color: 'var(--color-grey-45)',
                    fontSize: '20px',
                    lineHeight: '24px',
                    fontWeight: 600,
                  }}
                >
                  {t('report_issue_for')} {fieldName}
                </h5>
                <p
                  className='text-grey45'
                  style={{
                    margin: 0,
                    color: 'var(--color-grey-45)',
                    fontSize: '16px',
                    lineHeight: '16px',
                    fontWeight: 400,
                  }}
                >
                  {t('report_issue_title')}
                </p>
              </div>
            </div>
            <form onSubmit={handleSubmit}>
              <div
                className='modal-body'
                style={{
                  background: 'var(--color-grey-100)',
                  padding: '16px 24px 24px 24px',
                }}
              >
                {/* Assumption Selection - Dropdown */}
                <div className='form-group'>
                  <label
                    htmlFor='assumption'
                    style={{
                      color: '#4D5674',
                      fontFamily: 'Source Sans Pro',
                      fontWeight: 600,
                      fontStyle: 'normal',
                      fontSize: '14px',
                      lineHeight: '16px',
                      letterSpacing: '0%',
                    }}
                  >
                    {t('issue')}
                  </label>
                  <select
                    value={assumption}
                    onChange={(e) => handleAssumptionChange(e.target.value)}
                    className='form-control custom-select'
                    id='assumption'
                    style={{
                      background: 'var(--color-grey-100)',
                      color: assumption ? 'var(--color-grey-45)' : '#919DB4',
                      cursor: 'pointer',
                    }}
                  >
                    <option value='' disabled>
                      {t('select_placeholder')}
                    </option>
                    {assumptionOptions.map((option) => (
                      <option
                        key={option.value}
                        value={option.value}
                        disabled={option.isPlaceholder}
                        style={
                          option.isPlaceholder
                            ? {
                                fontWeight: 600,
                                color: 'var(--color-grey-45)',
                                backgroundColor: 'var(--color-grey-95)',
                              }
                            : {}
                        }
                      >
                        {option.title}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Sensor Selection - Show only when assumption is sensor-related */}
                {sensorRelatedAssumptions.includes(assumption) &&
                  availableSensors.length > 0 && (
                    <div className='form-group ml-1 mt-3 mb-3'>
                      <label
                        style={{
                          marginBottom: '4px',
                          color: '#4D5674',
                          fontFamily: 'Source Sans Pro',
                          fontWeight: 600,
                          fontStyle: 'normal',
                          fontSize: '14px',
                          lineHeight: '16px',
                          letterSpacing: '0%',
                        }}
                      >
                        {t('select_sensor')}
                      </label>
                      <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                        {availableSensors.map((sensor, index) => {
                          const isSelected = selectedSensorIds.includes(
                            sensor.id,
                          );
                          const { sensorColor, sensorType } = getSensorInfo(
                            sensor,
                            index,
                          );
                          const displayName =
                            sensor.transmitter_id + sensorType;

                          return (
                            <ChipButton
                              key={sensor.id}
                              label={displayName}
                              onClick={() => handleSensorSelect(sensor.id)}
                              isSelected={isSelected}
                              borderColor={sensorColor}
                              backgroundColor={sensorColor}
                              textColor={sensorColor}
                            />
                          );
                        })}
                      </div>
                    </div>
                  )}

                {/* Comment Input */}
                <div className='form-group ml-1 mt-3 mb-0'>
                  <label
                    htmlFor='issueComment'
                    style={{
                      margin: 0,
                      marginBottom: '4px',
                      color: '#4D5674',
                      fontFamily: 'Source Sans Pro',
                      fontWeight: 600,
                      fontStyle: 'normal',
                      fontSize: '14px',
                      lineHeight: '16px',
                      letterSpacing: '0%',
                    }}
                  >
                    {t('comment')}
                  </label>
                  <textarea
                    id='issueComment'
                    className='form-control text-style-running-16-regular text-grey45 placeholder'
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder={t('report_issue_placeholder')}
                    rows='6'
                    style={{
                      borderRadius: '3px',
                      border: '1px solid var(--color-grey-91)',
                      padding: '12px 16px',
                      background: 'var(--color-grey-100)',
                      resize: 'vertical',
                      minHeight: '120px',
                      fontFamily: 'Source Sans Pro, sans-serif',
                      fontSize: '16px',
                      lineHeight: '24px',
                      fontWeight: 400,
                      letterSpacing: '0%',
                    }}
                  />
                </div>
                {submitError && (
                  <div
                    className='mt-3'
                    role='alert'
                    style={{
                      width: '392px',
                      height: '32px',
                      maxWidth: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      borderRadius: '3px',
                      background: 'var(--color-status-alert-100)',
                      color: 'var(--color-status-alert-96)',
                      padding: '4px 16px 4px 8px',
                      fontFamily: 'Source Sans Pro, sans-serif',
                      fontSize: '14px',
                      fontWeight: 600,
                      lineHeight: '16px',
                      letterSpacing: '0%',
                    }}
                  >
                    <img
                      src={redAlertIcon}
                      alt=''
                      aria-hidden='true'
                      style={{ width: '24px', height: '24px' }}
                    />
                    <span>{submitError}</span>
                  </div>
                )}
              </div>
              <div
                className='modal-footer'
                style={{ background: 'var(--color-grey-100)' }}
              >
                <div className='container text-right'>
                  <button
                    type='button'
                    className='btn m-1 p-0 btn-lg footer-button-cancel'
                    data-dismiss='modal'
                    aria-label='Close'
                    onClick={handleClose}
                    disabled={isSubmitting}
                    style={{ fontWeight: 600 }}
                  >
                    {t('cancel')}
                  </button>
                  <button
                    type='submit'
                    className='btn m-0 p-0 btn-lg footer-button-save text-white'
                    disabled={!isFormValid || isSubmitting}
                    style={{ fontWeight: 600 }}
                  >
                    {isSubmitting ? t('reporting') : t('report')}
                    {isSubmitting && (
                      <span className='ml-2'>
                        <span
                          className='spinner-grow spinner-grow-sm'
                          role='status'
                          aria-hidden='true'
                        ></span>
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
      {successToast &&
        typeof document !== 'undefined' &&
        ReactDOM.createPortal(
          <div
            style={{
              position: 'fixed',
              right: '32px',
              bottom: '120px',
              zIndex: 2000,
            }}
          >
            <SuccessMessage
              t={t}
              eventType='report_issue'
              status='success'
              message={t('report_issue_success_message')}
              linkText={
                successToast?.url ? t('report_issue_view_on_monday') : null
              }
              linkUrl={successToast?.url || null}
              containerStyle={{
                width: '416px',
                maxWidth: 'calc(100vw - 48px)',
                borderRadius: '16px',
                border: '1px solid var(--color-grey-91)',
                background: 'var(--color-grey-100)',
                boxShadow: '0px 16px 40px rgba(77, 86, 116, 0.25)',
              }}
            />
          </div>,
          document.body,
        )}
    </>
  );
};

module.exports = ReportIssueModal;
